import { FC, useEffect, useState } from "react";
import ModalBoxCore from "../../../core-components/modal-box/ModalBoxCore";
import ModalHeader from "../../../core-components/modal-box/modal-header/ModalHeader";
import ModalBody from "../../../core-components/modal-box/modal-body/ModalBody";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	InputAdornment,
	Typography,
	CircularProgress,
	Divider,
} from "@mui/material";
import SearchIcon from "../../../../assets/svg-fc/b2b/SearchIcon";
import LabelInputField from "../../../core-components/label-input-field/LabelInputField";
import DateRangeFilter from "../../../reusable-components/date-range-filter/DateRangeFilter";
import { DateFilterEnum, EventTypeEnum } from "../../../../constants/enums";
import { ISelectedDateRangeFilter } from "../../../../types/commonTypes";
import { fDate, DEFAULT_DATE_FILTER_FORMAT } from "../../../../utils/formatTime";
import { IGetPreviousQuotesResponse, IGetPreviousQuotesResponseData } from "../../../../types/quoteFormTypes";
import { useAppDispatch } from "../../../../hooks/storeHooks";
import { getPreviousQuotesThunk } from "../../../../store/quote/quoteThunk";
import { useToast } from "../../../../context/ToasterContext";
import { getFullName } from "../../../../utils/commonUtils";
import { matchesSearch } from "../../../../utils/textSearch";
import CarretDown from "../../../../assets/svg-fc/CarretDown";
import { iBidType } from "../../../../types/eventTypes";
import { ICorporateEventBid } from "../../../../interfaces/corporateEvent";
import NoData from "../../../reusable-components/no-data/NoData";
import palette from "../../../../theme/palette";
import { calculateQuoteAmount } from "../../../../utils/quoteFormUtils";
import CrossIcon from "../../../../assets/svg-fc/CrossIcon";
import "./selectPreviousQuoteModal.scss";

interface ISelectPreviousQuoteModal {
	open: boolean;
	eventId: string;
	eventType: EventTypeEnum;
	selectedBidId: string;
	onBidClick: (bid: iBidType | ICorporateEventBid) => void;
	onClose: () => void;
}

const SelectPreviousQuoteModal: FC<ISelectPreviousQuoteModal> = ({
	open,
	eventId,
	eventType,
	selectedBidId,
	onBidClick,
	onClose,
}) => {
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const [selectedDateFilter, setSelectedDateFilter] = useState<ISelectedDateRangeFilter>({
		selectedDateFilter: DateFilterEnum.LAST_6_MONTHS,
		startDate: fDate(new Date(), DEFAULT_DATE_FILTER_FORMAT),
		endDate: fDate(new Date(), DEFAULT_DATE_FILTER_FORMAT),
	});
	const [selectedEventType, setSelectedEventType] = useState<EventTypeEnum>(eventType);
	const [loading, setLoading] = useState<boolean>(false);
	const [previousQuotes, setPreviousQuotes] = useState<IGetPreviousQuotesResponse[]>([]);
	const [filteredQuotes, setFilteredQuotes] = useState<IGetPreviousQuotesResponse[]>([]);
	const [searchText, setSearchText] = useState<string>("");

	useEffect(() => {
		return () => {
			setSelectedDateFilter({
				selectedDateFilter: DateFilterEnum.LAST_6_MONTHS,
				startDate: fDate(new Date(), DEFAULT_DATE_FILTER_FORMAT),
				endDate: fDate(new Date(), DEFAULT_DATE_FILTER_FORMAT),
			});
			setSelectedEventType(eventType);
			setPreviousQuotes([]);
			setFilteredQuotes([]);
			setSearchText("");
		};
	}, [open]);

	useEffect(() => {
		setLoading(true);
		dispatch(
			getPreviousQuotesThunk({
				eventType: selectedEventType,
				startDate: selectedDateFilter.startDate,
				endDate: selectedDateFilter.endDate,
			}),
		)
			.unwrap()
			.then((res: IGetPreviousQuotesResponseData) => {
				setPreviousQuotes(res?.previousQuotes ?? []);
				filterQuotesBySearch(res?.previousQuotes ?? []);
			})
			.catch(() => showToast("Something went wrong", undefined, "error"))
			.finally(() => setLoading(false));
	}, [selectedEventType, selectedDateFilter]);

	useEffect(() => {
		filterQuotesBySearch(previousQuotes);
	}, [searchText]);

	const filterQuotesBySearch = (allQuotes: IGetPreviousQuotesResponse[]) => {
		if (!searchText?.trim()) {
			setFilteredQuotes(allQuotes);
			return;
		}
		setFilteredQuotes(
			allQuotes?.filter(
				(quote) =>
					matchesSearch(searchText, [quote.name]) ||
					matchesSearch(searchText, [quote.customer?.firstName, quote.customer?.lastName]),
			),
		);
	};

	const handleBidClick = (bid: iBidType | ICorporateEventBid) => {
		onBidClick(bid);
		onClose();
	};

	const setCurrentEventStart = (quotes: IGetPreviousQuotesResponse[]) => {
		const currentEventIndex = quotes?.findIndex((event) => event?.id === eventId);
		if (currentEventIndex > -1) {
			return [
				...quotes?.slice(currentEventIndex, currentEventIndex + 1),
				...quotes?.slice(0, currentEventIndex),
				...quotes?.slice(currentEventIndex + 1),
			];
		}
		return quotes;
	};

	return (
		<ModalBoxCore open={open} onClose={onClose} className="selectPreviousQuoteModal">
			<ModalHeader modalHeaderText="Select a previous quote" closeModal={onClose} />
			<ModalBody>
				<Box className="searchAndFilterContainer">
					<LabelInputField
						id="search"
						type="text"
						placeholder="Search by event name or customer"
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						InputProps={{
							endAdornment: (
								<InputAdornment position="start">
									{searchText ? (
										<CrossIcon className="crossIcon" onClick={() => setSearchText("")} />
									) : (
										<SearchIcon />
									)}
								</InputAdornment>
							),
						}}
					/>
					<DateRangeFilter selectedFilter={selectedDateFilter} setSelectedFilter={setSelectedDateFilter} />
				</Box>
				<Box className="eventTypeButtons">
					<Box className="eventTypeButton" onClick={() => setSelectedEventType(EventTypeEnum.CUSTOM_EVENT)}>
						<Typography
							className={`eventTypeButtonText ${selectedEventType === EventTypeEnum.CUSTOM_EVENT ? "selected" : ""}`}
						>
							B2C (Private events)
						</Typography>
					</Box>
					<Box
						className="eventTypeButton"
						onClick={() => setSelectedEventType(EventTypeEnum.CORPORATE_EVENT)}
					>
						<Typography
							className={`eventTypeButtonText ${selectedEventType === EventTypeEnum.CORPORATE_EVENT ? "selected" : ""}`}
						>
							B2B (Corporate events)
						</Typography>
					</Box>
				</Box>
				<Box className="previousQuotesContainer">
					{loading ? (
						<Box className="loadingContainer">
							<CircularProgress size={24} />
						</Box>
					) : filteredQuotes.length > 0 ? (
						setCurrentEventStart(filteredQuotes)?.map((event, index) => {
							const bidsByDate = event?.bids?.reduce(
								(acc, bid) => {
									const date = fDate(bid.createdAt, "MMMM dd, yyyy");
									if (!acc[date]) {
										acc[date] = [];
									}
									acc[date].push(bid as iBidType & ICorporateEventBid);
									return acc;
								},
								{} as Record<string, typeof event.bids>,
							);

							const isCurrentAtStart = index === 0 && event?.id === eventId;

							return (
								<Box key={event?.id}>
									{isCurrentAtStart && <Typography className="thisEventText">This event</Typography>}
									<Accordion key={event?.id} className="previousQuote">
										<AccordionSummary
											className={`${event?.bids?.some((bid) => bid?.id === selectedBidId) ? "selected" : ""}`}
											expandIcon={<CarretDown />}
										>
											<Typography className="eventInfo">{`${fDate(event?.createdAt, "MMMM dd, yyyy")} ${event?.customer ? `- ${getFullName(event?.customer?.firstName, event?.customer?.lastName)}` : ""} - ${event?.name}`}</Typography>
										</AccordionSummary>
										<AccordionDetails className={`${isCurrentAtStart ? "currentEvent" : ""}`}>
											<Box className="bidInfoContainer">
												{Object.entries(bidsByDate || {}).map(([date, bids]) => (
													<Box key={date}>
														<Typography className="dateInfo">Quotes sent on {date}</Typography>
														{bids?.map((bid) => (
															<Typography
																key={bid?.id}
																className={`bidInfo ${bid?.id === selectedBidId ? "selected" : ""}`}
																onClick={() => handleBidClick(bid)}
															>
																AED {calculateQuoteAmount(bid)} | {fDate(bid?.createdAt, "hh:mm aa")}
															</Typography>
														))}
													</Box>
												))}
											</Box>
										</AccordionDetails>
									</Accordion>
									{isCurrentAtStart && <Divider color={palette.grey[200]} />}
								</Box>
							);
						})
					) : (
						<NoData
							className="noDataContainer"
							message="No quotes found matching your filters. Try adjusting your search or date range."
						/>
					)}
				</Box>
			</ModalBody>
		</ModalBoxCore>
	);
};

export default SelectPreviousQuoteModal;
